import React from "react";
import useExcelUpload, {
  generateRandomAmount
} from "../../utils/hooks/useExcelUpload";
import UploadExcelDialog from "../Schedules/utils/UploadExcelEnhanced";
import {
  taxAdjustmentOptions_tagging,
  investmentTaxAdjustmentOptions_tagging
} from "../../utils/static/detailsPL";

const sections = [
  "Revenue",
  "Other Income",
  "Cost of Sales",
  "Expenses",
  "Ignore"
];

const getInstructions = (companyType) => [
  ["Col", "Comments"],
  [
    "A",
    "[Optional] Account code from accounting system. Used for grouping accounts together in subsequent YAs"
  ],
  ["B", "Account Name. Max no. of characters [60]"],
  [
    "C",
    "Amount in account. \nThere is no need to flip the sign of the expenses and COGS as long as it is consistent."
  ],
  [
    "D",
    "Sections: A) Revenue; B) Cost of Sales; C) Expenses; D) Other income; E) Ignore. Ignore can be used to ignore certain summation and taxation lines in the P&L."
  ],
  [
    "E",
    "Merged Account Name; used to group several accounts together. If left blank, account name will be based on Col B."
  ],
  [
    "F",
    `Tax adjustment. Valid values are: ${
      companyType === "investment"
        ? investmentTaxAdjustmentOptions_tagging
            .map((option) => option.label)
            .join(", ")
        : taxAdjustmentOptions_tagging.map((option) => option.label).join(", ")
    }. If left blank, tax adjustment will be none.`
  ]
];

const DetailPLExcelUpload = ({
  company,
  onExcelAddRows,
  open,
  handleCloseDialog
}) => {
  const fileName =
    company.companyType === "investment"
      ? "investment_detailed_p&l_upload_template.xlsx"
      : "trading_detailed_p&l_upload_template.xlsx";
  const {
    selectedFile,
    uploading,
    alertMessage,
    alertSeverity,
    errorFileUrl,
    rowCount,
    handleCloseAlert,
    handleDownloadTemplate,
    handleFileChange,
    handleResetFile,
    handleUpload,
    getTemplateHeadersString
  } = useExcelUpload({
    fileName: fileName,
    instructions: getInstructions(company.companyType),
    sampleData: Array.from({ length: 10 }, (_, index) => ({
      "Account Code": `AC${index + 1}`,
      "Account Name": `Account Name ${index + 1}`,
      Amount: generateRandomAmount(),
      Section: index < 2 ? sections[0] : sections[index % sections.length],
      "Merged Account Name": index < 2 ? `Merged Account Name 1` : "",
      "Tax Adjustment":
        company.companyType === "investment"
          ? investmentTaxAdjustmentOptions_tagging[
              index % investmentTaxAdjustmentOptions_tagging.length
            ].label
          : taxAdjustmentOptions_tagging[
              index % taxAdjustmentOptions_tagging.length
            ].label
    })),
    validationSchema: [
      { type: "" },
      { type: "", required: true },
      { type: "number", round: true, required: true },
      { type: "text", required: true, fixedValue: sections },
      { type: "" },
      {
        type: "",
        fixedValue: (company.companyType === "investment"
          ? investmentTaxAdjustmentOptions_tagging.map((option) => option.label)
          : taxAdjustmentOptions_tagging.map((option) => option.label)
        ).concat("", undefined)
      }
    ],
    onUpload: (excelData, rowCount) => {
      if (rowCount > 0) {
        const updatedData = {};

        excelData.forEach((row) => {
          let [
            accountCode,
            accountName,
            Amount,
            Section,
            altDesc,
            taxAdjustment
          ] = row;

          Amount = Math.round(Amount);

          if (Section === "Ignore") {
            return;
          }

          const groupingKey = altDesc || accountName;

          if (!updatedData[Section]) {
            updatedData[Section] = {};
          }

          if (
            taxAdjustment === "" ||
            taxAdjustment === undefined ||
            taxAdjustment === "None"
          ) {
            taxAdjustment = "";
          } else if (
            !["None", "Deduct", "Added Back"].includes(taxAdjustment)
          ) {
            taxAdjustment =
              company.companyType === "investment"
                ? investmentTaxAdjustmentOptions_tagging.find(
                    (option) => option.label === taxAdjustment
                  )?.value
                : taxAdjustmentOptions_tagging.find(
                    (option) => option.label === taxAdjustment
                  )?.value;

            if (!taxAdjustment) {
              taxAdjustment = "";
            }
          }

          if (!updatedData[Section][groupingKey]) {
            updatedData[Section][groupingKey] = {
              accountCode,
              accountName: groupingKey,
              amount: parseInt(Amount) || 0,
              taxAdjustment,
              history: [
                {
                  name: accountName,
                  amount: parseInt(Amount),
                  accountCode,
                  taxAdjustment
                }
              ]
            };
          } else {
            updatedData[Section][groupingKey].amount += parseInt(Amount) || 0;
            updatedData[Section][groupingKey].history.push({
              name: accountName,
              amount: parseInt(Amount),
              accountCode,
              taxAdjustment
            });
          }
        });

        // Call onExcelAddRows once per section with all the rows for that section
        Object.keys(updatedData).forEach((section) => {
          const sectionRows = Object.values(updatedData[section]);
          if (onExcelAddRows && sectionRows.length > 0) {
            onExcelAddRows(section, sectionRows, company.companyType);
          }
        });
      }
    }
  });

  const taxAdjustmentLabels =
    company.companyType === "investment"
      ? investmentTaxAdjustmentOptions_tagging.map((o) => o.label)
      : taxAdjustmentOptions_tagging.map((o) => o.label);
  const fixedValueGroups = [
    {
      label: "Section",
      values: [...sections]
    },
    {
      label: "Tax Adjustment",
      values: ["", ...taxAdjustmentLabels]
    }
  ];

  return (
    <UploadExcelDialog
      title='Detailed P&L'
      open={open}
      selectedFile={selectedFile}
      uploading={uploading}
      alertMessage={alertMessage}
      alertSeverity={alertSeverity}
      errorFileUrl={errorFileUrl}
      validRows={rowCount}
      onClose={handleCloseDialog}
      onDownloadTemplate={handleDownloadTemplate}
      onFileChange={handleFileChange}
      onUpload={handleUpload}
      onCloseAlert={handleCloseAlert}
      onReset={handleResetFile}
      templateHeadersString={getTemplateHeadersString()}
      templateInstructions={getInstructions(company.companyType)}
      fixedValueGroups={fixedValueGroups}
    />
  );
};

export default DetailPLExcelUpload;
